/* EIGHT GOBLINS ARE ONE ROW.
 *
 * A tracker with twelve creatures on it is mostly the same creature, and the DM
 * reads past seven copies of it to find the one that matters. Roll20 has to keep
 * them as separate turns — they are separate tokens with separate hit points —
 * so this groups them for READING and for STEPPING, and never merges the
 * creatures themselves.
 *
 * What makes two rows the same creature:
 *   - neither is a party member (a PC is never grouped, whatever they are called)
 *   - neither is a timer
 *   - the same base name once a trailing number is taken off: "Goblin",
 *     "Goblin 2", "Goblin #3", "Goblin (4)" are one group
 *   - the same initiative score, because that is what makes one turn possible
 *
 * Deliberately NOT grouped: creatures with the same name on different scores.
 * They act at different times and showing them as one row would be a lie about
 * the order. The DM can still see them as two groups of the same name.
 *
 * Nothing here decides anything about hit points, conditions or tokens: those
 * stay per creature, keyed per token as they always were, and the row expands
 * to reach one of them.
 */
export interface GroupRow { id: string; name: string; kind: string; score: string; timer?: boolean }
export interface Group<T extends GroupRow> {
  key: string;
  /** the name without its number: "Goblin" */
  base: string;
  score: string;
  members: T[];
  /** the row that stands for the group in the list — the first in tracker order */
  lead: T;
}

/** "Goblin 3", "Goblin #3", "Goblin (3)" → "goblin". A name that is only a
 *  number keeps it, because "3" is a name somebody chose. */
export function baseName(name: string): string {
  const n = String(name || "").replace(/\s+/g, " ").trim();
  const stripped = n.replace(/\s*[#(\[]?\s*\d+\s*[)\]]?\s*$/, "").trim();
  return (stripped || n).toLowerCase();
}

/** Group what can be grouped, keeping tracker order. Rows that group with
 *  nothing come back as groups of one, so callers render one list. */
export function groupRows<T extends GroupRow>(rows: T[], enabled = true): Group<T>[] {
  const out: Group<T>[] = [];
  const index = new Map<string, Group<T>>();
  for (const row of rows) {
    const groupable = enabled && row.kind !== "pc" && !row.timer && row.score !== "";
    const key = groupable ? `${baseName(row.name)}@${row.score}` : `solo:${row.id}`;
    const had = index.get(key);
    if (had) { had.members.push(row); continue; }
    const g: Group<T> = { key, base: groupable ? baseName(row.name) : row.name, score: row.score, members: [row], lead: row };
    index.set(key, g);
    out.push(g);
  }
  return out;
}

/** The label a grouped row shows: the creature's own name when it is alone,
 *  else the shared name and how many there are. */
export function groupLabel<T extends GroupRow>(g: Group<T>): string {
  if (g.members.length === 1) return g.members[0].name;
  /* Capitalised from the first member rather than the lower-cased key, so
     "Goblin" is not "goblin". */
  const shown = g.members[0].name.replace(/\s*[#(\[]?\s*\d+\s*[)\]]?\s*$/, "").trim() || g.base;
  return `${shown} ×${g.members.length}`;
}

/** Which group holds this row. */
export const groupOf = <T extends GroupRow>(groups: Group<T>[], id: string): Group<T> | undefined =>
  groups.find((g) => g.members.some((m) => m.id === id));

/** How many turns Next must take to leave the group the current turn is in.
 *  One for an ordinary row; for a group, every member that has not acted yet.
 *
 *  This is what makes "eight goblins take one turn" true without lying to
 *  Roll20: each goblin's turn really does pass, one confirmed step at a time,
 *  so conditions tick and auras fire exactly as they would have. The DM simply
 *  presses Next once. */
export function stepsToLeaveGroup<T extends GroupRow>(order: T[], groups: Group<T>[], currentId: string): number {
  const at = order.findIndex((r) => r.id === currentId);
  if (at < 0) return 1;
  const g = groupOf(groups, currentId);
  if (!g || g.members.length < 2) return 1;
  let steps = 1;
  for (let i = at + 1; i < order.length; i++) {
    if (!g.members.some((m) => m.id === order[i].id)) break;
    steps += 1;
  }
  return steps;
}
